import { AfterViewInit, Component, Inject, OnDestroy, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { FormControl, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { Title } from '@angular/platform-browser';
import { ExerciceRow, ExercicesProvider } from '../../providers/exercices-provider';
import { FilesProvider } from '../../../files/providers/files-provider';
import { CurrentUserRolesProvider } from '../../../organizations/providers/current-user-roles-provider';
import { ExerciceRights, mapExerciceItemRightsToExerciceRights } from '../../providers/exercice-rights';
import { forkJoin, Observable, Subscription, switchMap, throwError } from 'rxjs';
import {
  MAT_DIALOG_DATA, MatDialogActions,
  MatDialogClose,
  MatDialogContent,
  MatDialogRef,
  MatDialogTitle
} from '@angular/material/dialog';
import {
  MatError,
  MatFormField,
  MatOption,
  MatSelect,
  MatSelectChange,
  MatSelectModule
} from '@angular/material/select';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Exercice } from "../../../common/models/exercice";
import { ExerciceCategory } from "../../../common/models/exercice-category";
import { Organization } from "../../../common/models/organization";
import { ImageData, InputImageComponent } from "../../../common/components/input-image/input-image";
import { FileData, InputFileComponent } from "../../../common/components/input-file/input-file";
import { DurationControlComponent } from "../../../common/components/duration-control/duration-control";
import { ConfigProvider } from "../../../config-provider.service";
import { CurrentUser } from "../../../common/providers/current-user";
import { UtilsProvider } from "../../../common/providers/utils-provider.service";
import { MatButton, MatIconButton } from "@angular/material/button";
import { MatTooltip } from "@angular/material/tooltip";
import { MatCheckboxModule } from "@angular/material/checkbox";
import { MatIcon } from "@angular/material/icon";
import { MatInput } from "@angular/material/input";
import { NgClass, NgForOf, NgIf } from "@angular/common";
import { MatRadioButton, MatRadioGroup } from "@angular/material/radio";
import { MatProgressBar } from "@angular/material/progress-bar";
import { MatHint } from "@angular/material/form-field";
import { UsersProvider } from "../../../users/providers/users-provider";
import { catchError, map } from "rxjs/operators";
import { CdkTextareaAutosize } from "@angular/cdk/text-field";
import { LangsProvider, Lang } from "../../../common/providers/langs-provider";
import { ChangeDetectorRef } from '@angular/core';
import {User} from "../../../common/models/user";
import {Library} from "../../../common/models/library";

@Component({
  selector: 'exercices-form',
  providers: [],
  styleUrls: ['./form.scss'],
  standalone: true,
  imports: [
    MatDialogTitle,
    MatIconButton,
    MatDialogClose,
    MatTooltip,
    TranslateModule,
    MatIcon,
    MatDialogContent,
    MatFormField,
    MatInput,
    ReactiveFormsModule,
    NgIf,
    MatError,
    MatRadioGroup,
    MatRadioButton,
    DurationControlComponent,
    MatSelectModule,
    MatCheckboxModule,
    MatOption,
    NgForOf,
    FormsModule,
    InputFileComponent,
    InputImageComponent,
    MatDialogActions,
    MatButton,
    MatHint,
    MatProgressBar,
    NgClass,
    CdkTextareaAutosize
  ],
  templateUrl: './form.html'
})
export class ExercicesFormComponent implements AfterViewInit, OnDestroy {
  public readonly EXERCICE_TYPE_REPEAT: number = Exercice.TYPE_REPEAT;
  public readonly EXERCICE_TYPE_DURATION: number = Exercice.TYPE_DURATION;

  public readonly MIN_DURATION: number = 0;
  public readonly NAME_MAX_LENGTH: number = 200;
  public readonly DESCRIPTION_MAX_LENGTH: number = 1000;
  public readonly CATEGORIES_MAX_COUNT: number = 3;
  public readonly VIDEO_MAX_DURATION: number = 15;
  public readonly VIDEO_LINK_MAX_LENGTH: number = 255;
  public readonly VIDEO_FILE_EXTENSIONS: string[] = ['avi', 'mp4', 'webm', 'ogg', 'flac', 'm4v', 'mov'];
  public readonly VIDEO_FILE_MAX_SIZE: number = 50; // Mo
  public readonly TAGS_MAX_LENGTH: number = 1000;
  public readonly IMAGE_MAX_SIZE: number = 5; // Mo
  public readonly IMAGE_MAX_HEIGHT: number = 5000;
  public readonly IMAGE_MAX_WIDTH: number = 8000;

  public readonly URL_REGEX: RegExp = /((([A-Za-z]{3,9}:(?:\/\/)?)(?:[\-;:&=\+\$,\w]+@)?[A-Za-z0-9\.\-]+|(?:www\.|[\-;:&=\+\$,\w]+@)[A-Za-z0-9\.\-]+)((?:\/[\+~%\/\.\w\-_]*)?\??(?:[\-\+=&;%@\.\w_]*)#?(?:[\.\!\/\\\w]*))?)/;
  public isLoading = false;
  public formTitle = '';
  public confirmButtonTitle = 'Save';
  public durationPlaceholder = 'Duration_of_repetitions';

  public availableExerciceCategories: ExerciceCategory[] = [];
  public imagePreview: any = null;
  public videoPreview: string | null = null;
  public isLangSelectable: boolean = true;
  public transcodingStartedAt: string | null = null;

  public organisation: Organization | null = null;
  public libraryId = 0;
  public exerciceId = 0;
  public type: string = Exercice.TYPE_MODEL;
  public categories: ExerciceCategory[] = [];
  public exerciceTypeTouched = false;
  public exerciceTypeFormControl: FormControl = new FormControl('', [Validators.required]);

  public fileType : 'video' | 'image' = 'image';
  public videoFileData: FileData = { current: null, new: null };
  public imageImageData: ImageData = { current: null, new: null };
  public saveInfo: string | null = null;
  public disableDuration = false;
  public hideDuration = false;
  public skipLangSelection = false;
  public displayTimeMode :'hms'|'hm'|'ms'|'s' = 'ms';

  public nameFormControl: FormControl = new FormControl('', [
    Validators.required
  ]);
  public descriptionFormControl: FormControl = new FormControl('');
  public categoriesFormControls: FormControl[] = [
    new FormControl([]),
    new FormControl([]),
    new FormControl([])
  ];
  public videoLinkFormControl: FormControl = new FormControl('', [
    Validators.pattern(this.URL_REGEX)
  ]);

  public tagsFormControl = new FormControl('', Validators.required);

  public duration = 0;
  @ViewChild('durationControl', {static: false}) public durationControl: DurationControlComponent | null = null;

  public languagesFormControl: FormControl = new FormControl<number[]>([], [(ctrl) => ctrl.value && ctrl.value.length > 0 ? null : { required: true }]);

  private data: any;

  private subscriptions: Subscription[] = [];
  public selectedLanguages: number[] = [];
  public availableLangs: Lang[] = [];

  constructor(
    public conf: ConfigProvider,
    public currentUser: CurrentUser,
    public utils: UtilsProvider,
    public dialog: MatDialogRef<ExercicesFormComponent>,
    private _toaster: MatSnackBar,
    private _title: Title,
    private _currentUserRoles: CurrentUserRolesProvider,
    private _exercicesProvider: ExercicesProvider,
    private usersProvider: UsersProvider,
    private _router: Router,
    private _route: ActivatedRoute,
    private _files: FilesProvider,
    private _translater: TranslateService,
    private _currentUser: CurrentUser,
    private langsProvider: LangsProvider,
    private cdr: ChangeDetectorRef,
    @Inject(MAT_DIALOG_DATA) private _data :any
  ) {
    this.organisation = _data.organization;
    this.saveInfo = _data.saveInfo;
    this.disableDuration = _data.disableDuration;
    this.hideDuration = _data.hideDuration;
    this.skipLangSelection = !!_data.skipLangSelection;

    this.loadExerciseCategories();

    this.data = _data;

    if (_data.exerciseRow) {
      // on restaure les données
      this.videoLinkFormControl.setValue(_data.exerciseRow.exercice.video_url);
      this.exerciceTypeFormControl.setValue(this._data.exerciseRow.exercice.exerciceType);
      this.onExerciceTypeChanged(_data.exerciseRow.exercice.exerciceType === 1 ? this.EXERCICE_TYPE_REPEAT : this.EXERCICE_TYPE_DURATION);
      this.libraryId = _data.exerciseRow.exercice.libraryId;
      this.exerciceId = _data.exerciseRow.exercice.id;
      this.type = _data.exerciseRow.exercice.type;
      this.nameFormControl.setValue(_data.exerciseRow.exercice.name);
      this.descriptionFormControl.setValue(_data.exerciseRow.exercice.description);
      this.duration = _data.exerciseRow.exercice.duration;
      this.videoFileData.current = _data.exerciseRow.exerciceVideo;
      this.transcodingStartedAt = _data.exerciseRow.exercice.transcoding_started_at || null;
      this.imageImageData.current = _data.exerciseRow.exerciceImage;
      this.tagsFormControl.setValue(_data.exerciseRow.exercice.tags);
      this.selectedLanguages = !this.skipLangSelection && _data.exerciseRow.exercice.availableLangIds || [];
      if (this.data.isLangSelectable !== undefined) {
        this.isLangSelectable = this.data.isLangSelectable;
      }

      this.initPreviewImages();

      for (let i = 0; i < _data.exerciseRow.categories.length; i++) {
        if (_data.exerciseRow.categories[i]) {
          this.categoriesFormControls[i].setValue(_data.exerciseRow.categories[i].id);
        }
      }
      if (this.type && this.type === Exercice.TYPE_PERSONALIZED) {
        this.tagsFormControl.removeValidators(Validators.required);
      }
    } else {
      // on met les données par défaut
      this.libraryId = _data.libraryId;
      this.videoFileData.current = null;
      this.imageImageData.current = null;
    }
    this.subscriptions.push(
      this.exerciceTypeFormControl.valueChanges.subscribe(() => { this.exerciceTypeTouched = true; })
    );

    this.formTitle = this._data.formTitle;
    this.confirmButtonTitle = this._data.confirmButtonTitle;
  }

  public ngAfterViewInit() {
    const langId = this.langsProvider.getBestLangId();

    if (!this.selectedLanguages.includes(langId)) {
      this.selectedLanguages.push(langId);
    }

    this.languagesFormControl.setValue(this.selectedLanguages);

    this.availableLangs = this.langsProvider.getLangs();
    this.cdr.detectChanges();
  }

  public toggleLanguage(langId: number, checked: boolean): void {
    if (checked) {
      this.selectedLanguages.push(langId);
    } else {
      const index = this.selectedLanguages.indexOf(langId);
      this.selectedLanguages.splice(index, 1);
    }

    this.languagesFormControl.setValue(this.selectedLanguages);
  }

  public isLangSelected(langId: number): boolean {
    return this.selectedLanguages.includes(langId);
  }

  public ngOnDestroy(): void {
    this.subscriptions.forEach((subscription) => subscription.unsubscribe());
  }

  public getSelectedCategoryName(i: number){
    const selectedCategory = this.availableExerciceCategories.find(category => category.id === this.categoriesFormControls[i].value);
    // TODO : FIX WHEN #53 is fixed
    return selectedCategory ? (selectedCategory.name ?? selectedCategory.translations[0].name) : '';
  }

  public getSelectedCategoryColor(i: number) {
    const selectedCategory = this.availableExerciceCategories.find(category => category.id === this.categoriesFormControls[i].value);
    return selectedCategory ? selectedCategory.colorHex : 'transparent';
  }

  public onExerciceTypeChanged(newType: number): void {
    this.durationPlaceholder = newType === Exercice.TYPE_REPEAT ? 'Duration_of_repetitions' : 'Total_duration';
    this.displayTimeMode = newType === Exercice.TYPE_REPEAT ? 'ms' : 'hms';
  }

  public onFileTypeChanged(newType: 'video' | 'image'): void {
    this.fileType = newType;
  }

  public onAccentChanged(event: MatSelectChange): void {
    if (event.value && event.value.length) {
      event.source.close();
    }
  }

  public onSaveClicked(): void {
    this.markAllAsTouched();

    this.exerciceTypeTouched = true;

    if (this.canSave()) {
      this.isLoading = true;
      this.save().subscribe({
        next: (result: ExerciceRow) => {
          this._toaster.open(this._translater.instant('Exercise_saved'), '', {duration: 5000});
          this.isLoading = false;
          this.dialog.close(result);
        },
        error: (error: any) => {
          this.onError(error);
        }
      });
    } else {
      this._toaster.open(this._translater.instant('Correct_form_errors'), '', { duration: 5000 });
    }
  }

  public markAllAsTouched(): void {
    this.nameFormControl.markAsTouched();
    this.exerciceTypeFormControl.markAsTouched();
    this.descriptionFormControl.markAsTouched();
    this.videoLinkFormControl.markAsTouched();
    this.tagsFormControl.markAsTouched();
    this.languagesFormControl.markAsTouched();

    for (let i = 0; i < this.categoriesFormControls.length; i++) {
      this.categoriesFormControls[i].markAsTouched();
    }
  }

  public canSave(): boolean {
    const canSaveResult = !this.isLoading
      && !this.nameFormControl.invalid
      && !this.exerciceTypeFormControl.invalid
      && (this.durationControl && !this.durationControl.invalid && this.duration >= this.MIN_DURATION)
      && !this.descriptionFormControl.invalid
      && !this.videoLinkFormControl.invalid
      && !this.tagsFormControl.invalid
      && !this.languagesFormControl.invalid;
    return canSaveResult ?? false;
  }

  public isAccentOptionDisabled(category: ExerciceCategory, formControl: FormControl): boolean {
    // si une option est sélectionné, on peut que désactivé cette option (et en sélectionner aucune autre)
    if (formControl.value !== category.id) {
      // return true;
    }

    // les options sélectionnées dans les autres formControl sont désactivées
    for (let i = 0; i < this.categoriesFormControls.length; i++) {
      if (this.categoriesFormControls[i] !== formControl) {
        if (this.categoriesFormControls[i].value === category.id) {
          return true;
        }
      }
    }

    return false;
  }

  public onAccentNoneClicked(formControl: FormControl): void {
    formControl.setValue(null);
  }

  private initPreviewImages(): void {
    this.imagePreview = this.data.exerciseRow.exerciceImage;
    this.videoPreview = this.data.exerciseRow.exercice?.video_thumbnail_path || (this.data.exerciseRow.exercice?.video_path ? this.imagePreview : null);
  }

  public onVideoDelete(): void {
    this.videoPreview = null;
    if (this.data.exerciseRow?.exercice) {
      this.data.exerciseRow.exercice.video_path = null;
    }
  }

  public onImageDelete(): void {
    if (this.data.exerciseRow?.exercice) {
      this.data.exerciseRow.exercice.image = null;
    }
  }

  private save(): Observable<ExerciceRow> {
    const exercice: Exercice = new Exercice({
      id: this.exerciceId,
      type: this.type,
      libraryId: this.libraryId,
      name: this.nameFormControl.value,
      exerciceType: this.exerciceTypeFormControl.value,
      duration: this.duration,
      description: this.descriptionFormControl.value,
      tags: this.tagsFormControl.value ?? undefined,
      video_url: this.data.exerciseRow?.exercice.video_url,
      video_path: this.data.exerciseRow?.exercice.video_path,
      video_thumbnail_path: this.data.exerciseRow?.exercice.video_thumbnail_path,
      video_is_transcoded: this.data.exerciseRow?.exercice.video_is_transcoded,
      image: this.data.exerciseRow?.exercice.image,
    });

    if (exercice.video_url || this.videoLinkFormControl.value) {
      exercice.video_url = this.videoLinkFormControl.value || exercice.video_url;
      exercice.video_path = undefined;
    } else if (exercice.video || this.videoFileData.new) {
      exercice.video = this.videoFileData.new || exercice.video;
      exercice.video_url = undefined;
      exercice.video_path = undefined;
    }

    exercice.image = this.imageImageData.new || exercice.image;

    exercice.libraryId = this.libraryId;
    for (let i = 0; i < this.categoriesFormControls.length; i++) {
      // @ts-ignore
      exercice['accent' + (i + 1)] = (this.categoriesFormControls[i].value && this.categoriesFormControls[i].value.length !== 0) ? this.categoriesFormControls[i].value : null;
    }

    if (!this.organisation?.id) throw new Error('No organisation id');

    const ignore_video = this.isVideoBeingTranscoded();

    return this._exercicesProvider.saveExercice(this.organisation.id, exercice, this.isLangSelectable ? this.selectedLanguages : undefined, ignore_video ? ignore_video : false).pipe(
      switchMap((savedExercice: Exercice) => {
        const row: ExerciceRow = {
          exercice: savedExercice,
          categories: [
            this.utils.findIn(savedExercice.accent1, this.availableExerciceCategories, 'id'),
            this.utils.findIn(savedExercice.accent2, this.availableExerciceCategories, 'id'),
            this.utils.findIn(savedExercice.accent3, this.availableExerciceCategories, 'id')
          ],
          exerciceImage: savedExercice.image || savedExercice.video_thumbnail_path,
          exerciceVideo: savedExercice.video_path,
          creater: null,
          rights: mapExerciceItemRightsToExerciceRights(savedExercice.rights ?? [])
        };

        return forkJoin([
          this.usersProvider.get(this.organisation?.id!, savedExercice.createrId!),
          this._exercicesProvider.getImageFile(this.organisation?.id!, row),
          this._exercicesProvider.getLibraries(this.organisation?.id!)
        ]).pipe(
          map(([creater, exerciceImage, libraries]: [User, any, Library[]]) => {

            const exeRights: ExerciceRights = new ExerciceRights(this.utils);
            exeRights.evaluateItemRight({
              user: this._currentUser.user!,
              organizationId: this.organisation?.id!,
              exercice: savedExercice,
              rolesIds: this._currentUserRoles.getRolesIds(this.organisation?.id!),
              libraries: libraries
            });

            row.creater = creater
            row.exerciceImage = exerciceImage
            row.rights = exeRights.getItemRightsList();

            return row;
          }),
        );
      }),
    );
  }

  private onError(error: any): void {
    this.isLoading = false;
    this._toaster.open(this._translater.instant('Error_unknown'), '', { duration: 5000 });
  }

  private loadExerciseCategories(): void {
    this._exercicesProvider.getExerciceCategories().subscribe({
      next: (categories: ExerciceCategory[]) => {
        this.availableExerciceCategories = categories;
      }
    });
  }

  public isVideoBeingTranscoded(): boolean {
    return this.data?.exerciseRow?.exercice?.isVideoInProgress ?? false;
  }

}
